import { FDDM_TAG } from '@/modules/floorplanDesign/constants';

export const LayerNames = {
  GENERATED_POIS: FDDM_TAG.POINT_OF_INTEREST,
  GENERATED_STATIONS: FDDM_TAG.STATION,
  TRACKS: FDDM_TAG.TRACK_PIECE,
  SWEPT_PATHS: FDDM_TAG.SWEPT_PATH,
  CONNECTIONS: FDDM_TAG.LAYER_CONNECTIONS,
  CONNECTION_BUBBLES: FDDM_TAG.LAYER_CONNECTION_BUBBLES,
  VEHICLE_LAYOUT_POI_CONNECTIONS: FDDM_TAG.VEHICLE_LAYOUT_POI_CONNECTION,
  ROADS: FDDM_TAG.LAYER_ROADS,
  HIGHWAYS: FDDM_TAG.LAYER_HIGHWAYS,
  ANGLED_HIGHWAYS: FDDM_TAG.LAYER_ANGLED_HIGHWAYS,
  AREAS: FDDM_TAG.LAYER_AREAS,
  INTAKE: FDDM_TAG.LAYER_INTAKE,
  DELIVERY: FDDM_TAG.LAYER_DELIVERY,
  STORAGE: FDDM_TAG.LAYER_STORAGE,
  CHARGING: FDDM_TAG.LAYER_CHARGING,
  PARKING: FDDM_TAG.LAYER_PARKING,
  MANUAL: FDDM_TAG.LAYER_MANUAL,
  EXTRACTION: FDDM_TAG.LAYER_EXTRACTION,
  INSERTION: FDDM_TAG.LAYER_INSERTION,
  PROCESS: FDDM_TAG.LAYER_PROCESS,
  INTERIOR: FDDM_TAG.LAYER_INTERIOR,
  WALLS: FDDM_TAG.LAYER_WALLS,
  OBSTACLES: FDDM_TAG.LAYER_OBSTACLES,
  REFERENCE: FDDM_TAG.LAYER_REFERENCE,
  LOGICAL_SPACES: FDDM_TAG.LAYER_LOGICAL_SPACES,
  FLOWS: FDDM_TAG.LAYER_FLOWS,
  FLOORPLAN: FDDM_TAG.LAYER_FLOORPLAN,
  NAMES: FDDM_TAG.LAYER_NAMES,
  AREA_NAMES: FDDM_TAG.LAYER_AREA_NAMES,
  HIGHWAY_NAMES: FDDM_TAG.LAYER_HIGHWAY_NAMES,
  OBSTACLE_NAMES: FDDM_TAG.LAYER_OBSTACLE_NAMES,
  ORIGIN: FDDM_TAG.ORIGIN,
  GROUPS: FDDM_TAG.LAYER_GROUPS,
} as const;

export type LayerNames = (typeof LayerNames)[keyof typeof LayerNames];

export const GeneratedLayerNames = {
  POIS: LayerNames.GENERATED_POIS,
  POI_CONNECTIONS: LayerNames.VEHICLE_LAYOUT_POI_CONNECTIONS,
  TRACKS: LayerNames.TRACKS,
  SWEPT_PATHS: LayerNames.SWEPT_PATHS,
} as const;

export type GeneratedLayerNames = (typeof GeneratedLayerNames)[keyof typeof GeneratedLayerNames];

const VEHICLE_GENERATED_LAYER_PREFIX = 'generated-vehicle:';
const VEHICLE_GENERATED_LAYER_SEPARATOR = ':';
const LAYER_NAMES: readonly string[] = Object.values(LayerNames);
const GENERATED_LAYER_NAMES: readonly string[] = Object.values(GeneratedLayerNames);

export type VehicleGeneratedLayerName =
  `${typeof VEHICLE_GENERATED_LAYER_PREFIX}${string}${typeof VEHICLE_GENERATED_LAYER_SEPARATOR}${GeneratedLayerNames}`;

export type LayerName = LayerNames | VehicleGeneratedLayerName;

export const createVehicleGeneratedLayerName = (
  vehicleTypeId: string,
  layerName: GeneratedLayerNames,
): VehicleGeneratedLayerName =>
  `${VEHICLE_GENERATED_LAYER_PREFIX}${vehicleTypeId}${VEHICLE_GENERATED_LAYER_SEPARATOR}${layerName}`;

export const isLayerNames = (value: string): value is LayerNames => LAYER_NAMES.includes(value);

export const isGeneratedLayerNames = (value: string): value is GeneratedLayerNames =>
  GENERATED_LAYER_NAMES.includes(value);

export const getGeneratedLayerName = (layerName: LayerName): GeneratedLayerNames | undefined => {
  if (isGeneratedLayerNames(layerName)) return layerName;

  return Object.values(GeneratedLayerNames).find((generatedLayerName) =>
    layerName.endsWith(`${VEHICLE_GENERATED_LAYER_SEPARATOR}${generatedLayerName}`),
  );
};

export const isVehicleGeneratedLayerName = (value: string): value is VehicleGeneratedLayerName => {
  if (!value.startsWith(VEHICLE_GENERATED_LAYER_PREFIX)) return false;

  return Object.values(GeneratedLayerNames).some((generatedLayerName) =>
    value.endsWith(`${VEHICLE_GENERATED_LAYER_SEPARATOR}${generatedLayerName}`),
  );
};

export const isLayerName = (value: string): value is LayerName =>
  isLayerNames(value) || isVehicleGeneratedLayerName(value);

export type LayerVisibilityMode = 'auto' | 'force_show' | 'force_hide';

export type Layer = {
  locked: boolean;
  visibilityMode: LayerVisibilityMode;
};

export type StoredLayer = {
  locked: boolean;
  visibilityMode?: LayerVisibilityMode;
};

export type Layers = Record<LayerNames, Layer> & Partial<Record<VehicleGeneratedLayerName, Layer>>;

export type StoredLayers = Partial<Record<LayerName, StoredLayer>>;

export type LayerType =
  | 'HIGHWAY'
  | 'ANGLED_HIGHWAY'
  | 'PILLAR'
  | 'BOX'
  | 'WARNING_ZONE'
  | 'WALL'
  | 'INTAKE'
  | 'DELIVERY'
  | 'STORAGE'
  | 'OFF_VEHICLE_PROCESSING'
  | 'ON_VEHICLE_PROCESSING'
  | 'CHARGING'
  | 'INSERTION'
  | 'EXTRACTION'
  | 'MANUAL'
  | 'INTAKE_AND_DELIVERY'
  | 'PARKING'
  | 'LOGICAL_SPACE'
  | 'GROUP';

export type ElementLayerType = {
  id: string;
  type: LayerType;
};
